/* Akor Kulağı: chord quality, root, degree in key, progression dictation, play the chord. */
import { audio } from '../../core/audio';
import type { ChordQuality, ChordRef } from '../../core/dsp';
import {
  chordInKey,
  chordLongName,
  chordSymbol,
  keyName,
  type Mode,
  parseChord,
  pcName,
  pretty,
  voicing,
} from '../../core/theory';
import { choice, type Rng, randInt } from '../../core/util';
import { ChordDiagram } from '../../ui/ChordDiagram';
import type {
  ChoiceQuestion,
  ChordQuestion,
  CustomQuestion,
  ExerciseConfig,
  InputKind,
  NotesQuestion,
  Question,
} from '../exercise/types';
import type { ChallengeDef, LevelDef } from '../levels';
import { Dictation, type DictationUi } from './Dictation';
import { MAJMIN, MAJMIN7, QFEEL, QLABEL } from './vocab';

const FUNC: Record<string, string> = {
  I: 'Tonik: ev, durgunluk.',
  i: 'Tonik: ev, durgunluk.',
  IV: 'Subdominant: evden uzaklaşma, açılma.',
  iv: 'Minör subdominant: hüzünlü bir uzaklaşma.',
  V: 'Dominant: gerilim, toniğe dönmek ister.',
  V7: "Dominant 7'li: güçlü gerilim, toniğe çözülür.",
  vi: 'Tonik ailesinden: hüzünlü bir "ev".',
  ii: "Subdominant ailesinden: genelde V'ye hazırlar.",
  iii: 'Tonik ailesinden, ara bir renk.',
  '♭VII': "Ödünç akor: rock ve pop'ta çok sevilir.",
  'V/vi': 'İkincil dominant: vi akoruna çekilir.',
  VI: 'Minörde VI: geniş, sıcak bir renk.',
  VII: "Minörde VII: III'e ya da i'ye götürür.",
  III: 'Minörde III: göreceli majörün toniği.',
  'vii°': 'Eksik akor: dominant görevi görür.',
  v: 'Minör v: yumuşak dominant.',
  'ii°': "Eksik ii: minörde V'ye hazırlar.",
};

const sym = (c: ChordRef, flat?: boolean) => chordSymbol(c.root, c.q, flat);

// ---------------------------------------------------------------- quality
const KAL_SETS: Record<number, ChordQuality[]> = {
  1: ['', 'm'],
  2: ['', 'm', 'dim', 'aug'],
  3: ['', 'm', '7', 'maj7', 'm7'],
  4: ['', 'm', 'sus2', 'sus4', '7'],
};

export function kaliteQ(lvl: number, rng: Rng, o: { maxMiss?: number } = {}): ChoiceQuestion {
  const qs = KAL_SETS[lvl] as ChordQuality[];
  const q = choice(qs, rng);
  const root = randInt(0, 11, rng);
  const chord = { root, q };
  const arp = () => audio.playNotes([...voicing(chord), null], { bpm: 150, inst: 'guitar', legato: 2.5 });
  return {
    type: 'choice',
    prompt: 'Bu akor hangi türde?',
    info: 'Akorun rengini dinle',
    options: qs.map((x) => ({ id: x, label: QLABEL[x] as string, sub: QFEEL[x] })),
    answer: q,
    maxMiss: o.maxMiss,
    columns: qs.length === 2 ? 2 : null,
    play: () => audio.playChord(chord),
    extraTools: [{ label: 'Arpej', icon: 'wave', run: () => arp() }],
    preview: (x) => audio.playChord({ root, q: x as ChordQuality }),
    answerText: () => `${QLABEL[q]} (${pretty(sym(chord))})`,
    explain: () => `Bu akor **${pretty(sym(chord))}**: ${chordLongName(root, q)}.`,
    reviewLabel: 'Akor türü',
  };
}

// ---------------------------------------------------------------- root
const KOK_OPEN = ['C', 'D', 'E', 'G', 'A', 'Am', 'Dm', 'Em'];
const KOK_SEV = ['E7', 'A7', 'D7', 'G7', 'C7', 'B7', 'Am7', 'Dm7', 'Em7', 'Cmaj7', 'Fmaj7', 'Gmaj7'];

export function kokQ(lvl: number, rng: Rng, o: { maxMiss?: number } = {}): NotesQuestion {
  let s: string;
  if (lvl === 1) s = choice(KOK_OPEN, rng);
  else if (lvl === 2) s = sym(choice(MAJMIN, rng));
  else s = choice(KOK_SEV, rng);
  const c = parseChord(s) as ChordRef;
  const v = voicing(s);
  const bass = v[0] as number;
  return {
    type: 'notes',
    target: [bass],
    flex: true,
    fb: { from: 0, to: 12 },
    maxMiss: o.maxMiss,
    prompt: 'Akorun kök notasını gitarda çal',
    info: 'İpucu: en kalın sesi mırıldan',
    screenHint: 'Akorun kök notasını sapta bul ve üzerine tıkla.',
    play: () => audio.playChord(s),
    playSlow: () => audio.playNotes(v, { bpm: 110, inst: 'guitar', legato: 3 }),
    hints: [
      {
        label: 'Bası dinle',
        run: () => {
          audio.playNote(bass, { inst: 'guitar', dur: 1.5 });
          return 'Bu akorun en kalın notası.';
        },
      },
      { label: 'Sapta göster', run: (api) => api.reveal() },
    ],
    answerText: () => `${pretty(s)} → kök: ${pcName(c.root)}`,
    explain: () => `Akor **${pretty(s)}**, kökü ${pcName(c.root)}.`,
    reviewLabel: 'Kök nota',
  };
}

// ---------------------------------------------------------------- degree in key
const DER_SETS: Record<number, { mode: Mode; degs: string[] }> = {
  1: { mode: 'major', degs: ['I', 'IV', 'V'] },
  2: { mode: 'major', degs: ['I', 'IV', 'V', 'vi'] },
  3: { mode: 'major', degs: ['I', 'ii', 'iii', 'IV', 'V', 'vi'] },
  4: { mode: 'minor', degs: ['i', 'iv', 'V', 'VI', 'VII', 'III'] },
  5: { mode: 'major', degs: ['I', 'IV', 'V', 'vi', '♭VII', 'iv', 'V/vi'] },
};
const MAJ_KEYS = [0, 7, 2, 9, 4, 5];
const MIN_KEYS = [9, 4, 2];

export function dereceQ(lvl: number, rng: Rng, o: { maxMiss?: number } = {}): ChoiceQuestion {
  const set = DER_SETS[lvl] as { mode: Mode; degs: string[] };
  const tonic = choice(set.mode === 'major' ? MAJ_KEYS : MIN_KEYS, rng);
  const deg = choice(set.degs, rng);
  const c = chordInKey(deg, tonic, set.mode);
  const cad = () => audio.cadenceEvents(tonic, set.mode, { bpm: 120 });
  const playAll = () => {
    const cc = cad();
    const ev = cc.ev.concat(audio.chordEvents({ root: c.root, q: c.q }, cc.length + 0.45, 2.2));
    return audio.play(ev, { length: cc.length + 2.7 });
  };
  return {
    type: 'choice',
    prompt: 'Kadanstan sonra çalan akor tonalitenin hangi derecesi?',
    info: `${keyName(tonic, set.mode)} · önce kadans, sonra akor`,
    options: set.degs.map((d) => ({ id: d, label: d, sub: pretty(chordInKey(d, tonic, set.mode).sym) })),
    answer: deg,
    maxMiss: o.maxMiss,
    play: playAll,
    extraTools: [
      {
        label: 'Kadans',
        icon: 'loop',
        run: () => {
          const cc = cad();
          return audio.play(cc.ev, { length: cc.length });
        },
      },
      { label: 'Sadece akor', icon: 'play', run: () => audio.playChord({ root: c.root, q: c.q }) },
    ],
    preview: (d) => {
      const x = chordInKey(String(d), tonic, set.mode);
      return audio.playChord({ root: x.root, q: x.q });
    },
    answerText: () => `${deg} · ${pretty(c.sym)}`,
    explain: () => `Derece **${deg}**, akor **${pretty(c.sym)}**. ${FUNC[deg] || ''}`,
    reviewLabel: `${keyName(tonic, set.mode)} içinde derece`,
  };
}

// ---------------------------------------------------------------- progression dictation
const DIZI: Record<number, { mode: Mode; degs: string[]; tpl: string[][] }> = {
  1: {
    mode: 'major',
    degs: ['I', 'IV', 'V'],
    tpl: [
      ['I', 'IV', 'V', 'I'],
      ['I', 'V', 'IV', 'I'],
      ['I', 'IV', 'I', 'V'],
      ['I', 'I', 'IV', 'V'],
      ['I', 'V', 'I', 'IV'],
      ['I', 'IV', 'V', 'V'],
      ['I', 'V', 'V', 'I'],
      ['I', 'IV', 'IV', 'V'],
    ],
  },
  2: {
    mode: 'major',
    degs: ['I', 'IV', 'V', 'vi'],
    tpl: [
      ['I', 'V', 'vi', 'IV'],
      ['I', 'vi', 'IV', 'V'],
      ['I', 'IV', 'vi', 'V'],
      ['I', 'vi', 'V', 'IV'],
      ['I', 'IV', 'I', 'V'],
      ['I', 'V', 'IV', 'vi'],
      ['I', 'vi', 'IV', 'I'],
    ],
  },
  3: {
    mode: 'major',
    degs: ['I', 'ii', 'iii', 'IV', 'V', 'vi'],
    tpl: [
      ['I', 'ii', 'V', 'I'],
      ['I', 'iii', 'IV', 'V'],
      ['I', 'vi', 'ii', 'V'],
      ['I', 'IV', 'ii', 'V'],
      ['I', 'iii', 'vi', 'IV'],
      ['I', 'ii', 'IV', 'V'],
      ['I', 'vi', 'iii', 'IV'],
    ],
  },
  4: {
    mode: 'minor',
    degs: ['i', 'iv', 'V', 'VI', 'VII', 'III'],
    tpl: [
      ['i', 'iv', 'V', 'i'],
      ['i', 'VI', 'III', 'VII'],
      ['i', 'VII', 'VI', 'V'],
      ['i', 'iv', 'VII', 'III'],
      ['i', 'VI', 'iv', 'V'],
      ['i', 'iv', 'i', 'V'],
      ['i', 'VII', 'VI', 'VII'],
    ],
  },
  5: {
    mode: 'major',
    degs: ['I', 'ii', 'IV', 'V', 'vi', '♭VII'],
    tpl: [
      ['I', 'V', 'vi', 'IV', 'I', '♭VII', 'IV', 'I'],
      ['I', 'vi', 'ii', 'V', 'I', 'IV', 'V', 'I'],
      ['I', '♭VII', 'IV', 'I', 'vi', 'ii', 'V', 'V'],
      ['I', 'IV', 'vi', 'V', 'I', 'ii', '♭VII', 'I'],
      ['I', 'ii', 'IV', 'V', 'vi', 'IV', '♭VII', 'I'],
    ],
  },
};

export function diziQ(lvl: number, rng: Rng, o: { maxMiss?: number } = {}): CustomQuestion {
  const set = DIZI[lvl] as (typeof DIZI)[number];
  const tonic = choice(set.mode === 'major' ? [0, 7, 2, 9, 4] : MIN_KEYS, rng);
  const degs = choice(set.tpl, rng);
  const chords = degs.map((d) => chordInKey(d, tonic, set.mode));
  const ui: DictationUi = { onBar: null };
  const progPlay = (onlyIdx: number | null) => {
    const list = onlyIdx == null ? chords : [chords[onlyIdx], chords[onlyIdx]];
    return audio.playProg(
      list.map((c) => (c ? { root: c.root, q: c.q } : null)),
      {
        bpm: 92,
        bass: true,
        pattern: 'D-D-D-DU',
        onCue: (cue) => {
          if (cue.type === 'bar') ui.onBar?.(onlyIdx == null ? cue.index : onlyIdx);
        },
        onEnd: () => ui.onBar?.(null),
      },
    );
  };
  return {
    type: 'custom',
    prompt: `${degs.length} akorluk diziyi çıkar`,
    info: `${keyName(tonic, set.mode)} · ilk akor verildi`,
    play: () => progPlay(null),
    maxMiss: o.maxMiss || 3,
    points: 1,
    answerText: () => degs.map((d, i) => `${pretty((chords[i] as { sym: string }).sym)} (${d})`).join(' · '),
    reviewLabel: `${keyName(tonic, set.mode)} dizisi`,
    render: (api) => (
      <Dictation
        api={api}
        degs={degs}
        chords={chords}
        palette={set.degs}
        tonic={tonic}
        mode={set.mode}
        ui={ui}
        playBar={(i) => progPlay(i)}
      />
    ),
  };
}

// ---------------------------------------------------------------- play the chord on guitar
const CAL_SETS: Record<number, string[]> = {
  1: ['G', 'C', 'D', 'Em'],
  2: ['C', 'D', 'E', 'G', 'A', 'Am', 'Dm', 'Em'],
  3: ['E7', 'A7', 'D7', 'G7', 'B7', 'C7', 'E', 'A', 'D', 'G'],
};

export function calQ(lvl: number, rng: Rng, o: { maxMiss?: number } = {}): ChordQuestion {
  let s: string;
  let pool: string[];
  let pre: number | null = null;
  let info: string;
  if (lvl === 4) {
    const key = choice(
      [
        { t: 7, chords: ['G', 'Am', 'Bm', 'C', 'D', 'Em'] },
        { t: 0, chords: ['C', 'Dm', 'Em', 'F', 'G', 'Am'] },
      ],
      rng,
    );
    s = choice(key.chords.slice(1), rng);
    pool = key.chords;
    pre = key.t;
    info = `${keyName(key.t, 'major')} · önce kadans`;
  } else {
    pool = CAL_SETS[lvl] as string[];
    s = choice(pool, rng);
    info = `${pool.length} akor arasından`;
  }
  const c = parseChord(s) as ChordRef;
  const playIt = () => {
    if (pre == null) return audio.playChord(s);
    const cc = audio.cadenceEvents(pre, 'major', { bpm: 120 });
    return audio.play(cc.ev.concat(audio.chordEvents(s, cc.length + 0.45, 2.2)), { length: cc.length + 2.7 });
  };
  return {
    type: 'chord',
    target: { root: c.root, q: c.q, sym: s },
    vocab: lvl === 3 ? MAJMIN7 : MAJMIN,
    maxMiss: o.maxMiss,
    screenOptions: pool.map((x) => {
      const p = parseChord(x) as ChordRef;
      return { root: p.root, q: p.q, sym: x };
    }),
    prompt: 'Duyduğun akoru gitarda bul ve çal',
    info,
    play: playIt,
    hints: [
      { label: 'Kök nota', run: () => `Kök nota: **${pcName(c.root)}**.` },
      { label: 'Tür', run: () => `Akor türü: **${QLABEL[c.q]}**.` },
    ],
    answerText: () => pretty(s),
    explain: () => (
      <span>
        Akor: <strong>{pretty(s)}</strong>
        <ChordDiagram chord={s} />
      </span>
    ),
    reviewLabel: 'Akoru çal',
  };
}

// ---------------------------------------------------------------- definitions
interface AkorGroup {
  id: string;
  title: string;
  sub: string;
  input: InputKind;
  total: number;
  levels: AkorLevel[];
  make: (lv: AkorLevel, rng: Rng) => Question;
}
export interface AkorLevel extends LevelDef {
  group: AkorGroup;
}

function group(g: Omit<AkorGroup, 'levels'> & { levels: { n: number; title: string; desc: string }[] }): AkorGroup {
  const out = g as unknown as AkorGroup;
  out.levels = g.levels.map((l) => ({
    ...l,
    group: out,
    key: `akor.egz.${g.id}.${l.n}`,
    route: `akor.egz.${g.id}.${l.n}`,
  }));
  return out;
}

export const EX_GROUPS: AkorGroup[] = [
  group({
    id: 'kalite',
    title: 'Akor türü',
    sub: 'Majör mü minör mü? Akorun rengini tanı.',
    input: 'none',
    total: 10,
    levels: [
      { n: 1, title: 'Majör ve minör', desc: 'İki temel renk.' },
      { n: 2, title: '+ eksik ve artık', desc: 'Dört üçlü akor türü.' },
      { n: 3, title: "7'li akorlar", desc: "Majör, minör, dominant 7'li, majör 7'li, minör 7'li." },
      { n: 4, title: 'Sus akorları', desc: "Sus2, sus4 ve dominant 7'li." },
    ],
    make: (lv, rng) => kaliteQ(lv.n, rng),
  }),
  group({
    id: 'kok',
    title: 'Kök notayı bul',
    sub: 'Akoru dinle, bas notasını gitarda bul. Mikrofon kapalıysa sapa tıkla.',
    input: 'notes',
    total: 8,
    levels: [
      { n: 1, title: 'Açık akorlar', desc: 'C, D, E, G, A, Am, Dm, Em.' },
      { n: 2, title: 'Bütün tonlar', desc: 'Barre şekilleriyle her kökten majör ve minör.' },
      { n: 3, title: "7'li akorlar", desc: "7'li akorlarda kökü duymak." },
    ],
    make: (lv, rng) => kokQ(lv.n, rng),
  }),
  group({
    id: 'derece',
    title: 'Derece tanıma',
    sub: 'Tonalite içinde akorun görevini duy: I mi, IV mü, V mi?',
    input: 'none',
    total: 10,
    levels: [
      { n: 1, title: 'I, IV, V', desc: 'Üç ana akor.' },
      { n: 2, title: '+ vi', desc: "Pop'un dört akoru." },
      { n: 3, title: 'Bütün majör aile', desc: 'I, ii, iii, IV, V, vi.' },
      { n: 4, title: 'Minör tonalite', desc: 'i, iv, V, VI, VII, III.' },
      { n: 5, title: 'Renkli akorlar', desc: '♭VII, ödünç iv ve ikincil dominant.' },
    ],
    make: (lv, rng) => dereceQ(lv.n, rng),
  }),
  group({
    id: 'dizi',
    title: 'Dizi çıkar',
    sub: 'Dört (ya da sekiz) akorluk bir diziyi baştan sona çıkar. Şarkı çıkarmanın provası.',
    input: 'none',
    total: 5,
    levels: [
      { n: 1, title: 'I, IV, V', desc: 'Üç akorlu diziler.' },
      { n: 2, title: '+ vi', desc: 'Pop dizileri.' },
      { n: 3, title: '+ ii, iii', desc: 'Bütün majör aile.' },
      { n: 4, title: 'Minör diziler', desc: "Endülüs kadansı ve Türk pop/rock'ta yaygın i–iv–V." },
      { n: 5, title: 'Sekiz akor', desc: 'Uzun dizilerle ♭VII.' },
    ],
    make: (lv, rng) => diziQ(lv.n, rng),
  }),
  group({
    id: 'cal',
    title: 'Gitarla çal',
    sub: 'Akoru dinle, gitarda bul ve çal. Mikrofon akoru tanıyınca onaylar.',
    input: 'chord',
    total: 8,
    levels: [
      { n: 1, title: 'G ailesi', desc: 'G, C, D, Em.' },
      { n: 2, title: 'Açık akorlar', desc: 'Sekiz temel açık akor.' },
      { n: 3, title: "7'li akorlar", desc: "Dominant 7'liler ve majör akorlar." },
      { n: 4, title: 'Tonalite içinde', desc: 'Kadanstan sonra gelen akoru çal.' },
    ],
    make: (lv, rng) => calQ(lv.n, rng),
  }),
];

export const LEVELS: AkorLevel[] = EX_GROUPS.flatMap((g) => g.levels);

export const CHALLENGES: ChallengeDef[] = [
  {
    id: 'avci',
    title: 'Akor avcısı',
    desc: '60 saniyede majör mü minör mü? Tek deneme hakkı.',
    badge: '60 sn',
    input: 'none',
    key: 'akor.meydan.avci',
    route: 'akor.meydan.avci',
    cfg: {
      timeLimit: 60,
      autoAdvance: true,
      starAt: [10, 16, 22],
      make: (_i, _st, rng) => kaliteQ(1, rng, { maxMiss: 1 }),
    },
  },
  {
    id: 'dizi',
    title: 'Dizi ustası',
    desc: 'Beş pop dizisi, her biri için tek kontrol hakkı.',
    badge: '5 dizi',
    input: 'none',
    key: 'akor.meydan.dizi',
    route: 'akor.meydan.dizi',
    cfg: { total: 5, starAt: [2, 4, 5], make: (_i, _st, rng) => diziQ(2, rng, { maxMiss: 1 }) },
  },
];

const RULES: Record<string, string[]> = {
  cal: [
    'Akoru dinle, gitarında bul ve birkaç kez çal.',
    'Gitarın akortlu olsun; algılama akort bozukluğuna duyarlıdır.',
    'Mikrofon kapalıysa duyduğun akoru seçerek cevap verirsin.',
  ],
  dizi: [
    'İlk akor verilir; tonalite bilgisi üstte yazar.',
    'Bas sesini takip et: akor değişimlerini en iyi o gösterir.',
  ],
};

export function levelConfig(level: AkorLevel): ExerciseConfig {
  const g = level.group;
  const next = g.levels[g.levels.indexOf(level) + 1];
  return {
    key: level.key,
    module: 'akor',
    back: 'akor.antrenman',
    title: `${g.title}\u00a0· ${level.title}`, // the dot stays with the group name when the title wraps
    levelNo: level.n,
    desc: level.desc,
    total: g.total,
    input: g.input,
    next: next ? next.route : null,
    rules: RULES[g.id] ?? null,
    make: () => g.make(level, Math.random),
  };
}
